import React, { useReducer, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AccompanyingGuest from './AccompanyingGuest';
import Modal from './Modal';
import { createGuest } from '../services/guestApi';

const predefinedAllergies = [
  { id: 'gluten', icon: '🌾' },
  { id: 'crustaceans', icon: '🦐' },
  { id: 'eggs', icon: '🥚' },
  { id: 'fish', icon: '🐟' },
  { id: 'peanuts', icon: '🥜' },
  { id: 'soybeans', icon: '🌱' },
  { id: 'milk', icon: '🥛' },
  { id: 'nuts', icon: '🌰' },
  { id: 'celery', icon: '🥒' },
  { id: 'mustard', icon: '🌶☀' },
  { id: 'sesame', icon: '⚪' },
  { id: 'sulphites', icon: '🌫☀' },
  { id: 'lupin', icon: '🌼' },
  { id: 'molluscs', icon: '🐚' },
];

const initialState = {
  firstName: '',
  lastName: '',
  isAttending: false,
  menuOption: 'normal',
  busService: false,
  accompanyingCount: 0,
  accompanyingGuests: [],
  allergies: [],
  customAllergy: '',
};

const guestTemplate = {
  firstName: '',
  lastName: '',
  menuOption: 'normal',
  busService: false,
  allergies: [],
  customAllergy: '',
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'UPDATE_FIELD':
      return { ...state, [action.payload.name]: action.payload.value };
    case 'TOGGLE_ALLERGY': {
      const { allergy } = action.payload;
      const allergies = state.allergies.includes(allergy)
        ? state.allergies.filter(a => a !== allergy)
        : [...state.allergies, allergy];
      return { ...state, allergies };
    }
    case 'ADD_ACCOMPANYING_GUEST':
      return {
        ...state,
        accompanyingGuests: [...state.accompanyingGuests, { ...guestTemplate }],
      };
    case 'UPDATE_ACCOMPANYING_GUEST': {
      const { index, field, value } = action.payload;
      const updatedGuests = state.accompanyingGuests.map((guest, i) =>
        i === index ? { ...guest, [field]: value } : guest
      );
      return { ...state, accompanyingGuests: updatedGuests };
    }
    case 'REMOVE_ACCOMPANYING_GUEST': {
      const { index } = action.payload;
      const accompanyingGuests = state.accompanyingGuests.filter((_, i) => i !== index);
      return { ...state, accompanyingGuests };
    }
    case 'RESET_FORM':
      return initialState;
    default:
      return state;
  }
};

const GuestForm = () => {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [recentlySubmitted, setRecentlySubmitted] = useState(new Set());

  useEffect(() => {
    if (message) {
      setIsModalOpen(true);
      const timer = setTimeout(() => {
        setIsModalOpen(false);
        setMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Limpiar la lista de enviados recientemente cuando el componente se monta
  useEffect(() => {
    const handleBeforeUnload = () => {
      setRecentlySubmitted(new Set());
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setMessage('');
  };

  const handleFieldChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    dispatch({
      type: 'UPDATE_FIELD',
      payload: { name, value: type === 'checkbox' ? checked : value },
    });
  }, []);

  const toggleAllergy = useCallback((allergy) => {
    dispatch({ type: 'TOGGLE_ALLERGY', payload: { allergy } });
  }, []);

  const handleAccompanyingGuestChange = useCallback((index, field, value) => {
    dispatch({
      type: 'UPDATE_ACCOMPANYING_GUEST',
      payload: { index, field, value },
    });
  }, []);

  const addAccompanyingGuest = useCallback(() => {
    dispatch({ type: 'ADD_ACCOMPANYING_GUEST' });
  }, []);

  const removeAccompanyingGuest = useCallback((index) => {
    dispatch({ type: 'REMOVE_ACCOMPANYING_GUEST', payload: { index } });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!state.firstName.trim() || !state.lastName.trim()) {
      setIsSuccess(false);
      setMessage(t('guestForm.nameLastNameRequired') || 'Por favor, ingrese su nombre y apellido.');
      return; 
    }

    // Verificación adicional para evitar envíos duplicados en la misma sesión
    const guestKey = `${state.firstName.trim().toLowerCase()}-${state.lastName.trim().toLowerCase()}`;
    if (recentlySubmitted.has(guestKey)) {
      setIsSuccess(false);
      setMessage(t('guestForm.error409Message') || 'Este invitado ya está registrado.');
      return;
    }
  
    setIsLoading(true);
    const guestData = { ...state, isAttending: true };
  
    try {
      await createGuest(guestData);
      setIsSuccess(true);
      setMessage(t('guestForm.successMessage'));
      
      // Agregar a la lista de recientemente enviados
      setRecentlySubmitted(prev => new Set([...prev, guestKey]));
      
      dispatch({ type: 'RESET_FORM' });
    } catch (error) {
      setIsSuccess(false);
      if (error.response) {
        switch (error.response.status) {
          case 409:
            // También agregar a la lista local si el servidor confirma que existe
            setRecentlySubmitted(prev => new Set([...prev, guestKey]));
            setMessage(t('guestForm.error409Message') || 'Este invitado ya está registrado.');
            break;
          case 400:
            setMessage(t('guestForm.error400Message') || 'Por favor, verifique los datos ingresados.');
            break;
          case 500:
            setMessage(t('guestForm.error500Message') || 'Ha ocurrido un error en el servidor. Por favor, intente más tarde.');
            break;
          default:
            setMessage(t('guestForm.errorMessage') || 'Ha ocurrido un error. Por favor, intente nuevamente.');
        }
      } else if (error.request) {
        setMessage(t('guestForm.networkError') || 'Error de conexión. Por favor, verifique su conexión a internet.');
      } else {
        setMessage(error.message || t('guestForm.errorMessage') || 'Ha ocurrido un error. Por favor, intente nuevamente.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="relative py-8" id="form">
      <h2 className="text-4xl font-bold font-cursive mb-6 text-center text-beast-brown-800">
        {t('guestForm.title')}
      </h2>
      <div className="relative w-full max-w-2xl mx-auto p-6 rounded-xl bg-castle-cream-200 border-2 border-royal-gold-500 text-beast-brown-700">
        <form onSubmit={handleSubmit} className="space-y-6">
          <input
            type="text"
            name="firstName"
            placeholder={t('guestForm.firstNamePlaceholder')}
            value={state.firstName}
            onChange={handleFieldChange}
            className="w-full  border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-royal-gold-500"
            required
          />
          <input
            type="text"
            name="lastName"
            placeholder={t('guestForm.lastNamePlaceholder')}
            value={state.lastName}
            onChange={handleFieldChange}
            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-royal-gold-500"
            required
          />
          <div className="w-full sm:w-fit">
            <div className="flex items-center border border-gray-300 rounded-lg px-4 py-2 w-full sm:w-[250px] bg-white">
              <label className="text-beast-brown-700 flex-grow">{t('guestForm.requiresBus')}</label>
              <div className="flex-shrink-0 ml-4">
                <input
                  type="checkbox"
                  name="busService"
                  checked={state.busService}
                  onChange={handleFieldChange}
                  className="h-5 w-5 text-royal-gold-600"
                />
              </div>
            </div>
          </div>
          <div className="w-full sm:w-fit">
            <label className="block text-lg font-medium mb-2">Tipo de menú</label>
            <select
              name="menuOption"
              value={state.menuOption}
              onChange={handleFieldChange}
              className="w-full sm:w-[250px] border border-gray-300 rounded-lg px-4 py-2 bg-white text-beast-brown-700 focus:outline-none focus:ring-2 focus:ring-royal-gold-500 cursor-pointer"
              required
            >
              <option value="normal">Normal</option>
              <option value="vegan">Vegano</option>
              <option value="child">Infantil</option>
            </select>
          </div>
          <div className="space-y-4">
            <h3 className="text-lg font-medium">{t('guestForm.allergies')}</h3>
            {predefinedAllergies.map((allergy) => (
              <label key={allergy.id} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={state.allergies.includes(allergy.id)}
                  onChange={() => toggleAllergy(allergy.id)}
                  className="h-5 w-5 text-royal-gold-600"
                />
                <span>
                  {allergy.icon} {t(`guestForm.allergyList.${allergy.id}`)}
                </span>
              </label>
            ))}
            <input
              type="text"
              name="customAllergy"
              placeholder={t('guestForm.customAllergyPlaceholder')}
              value={state.customAllergy}
              onChange={handleFieldChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-royal-gold-500"
            />
          </div>
          <div className="space-y-4">
            {state.accompanyingGuests.map((guest, index) => (
              <AccompanyingGuest
                key={index}
                index={index}
                guest={guest}
                onChange={handleAccompanyingGuestChange}
                onRemove={removeAccompanyingGuest}
              />
            ))}
            <button
              type="button"
              onClick={addAccompanyingGuest}
              className="bg-royal-gold-400 hover:bg-royal-gold-500 text-castle-cream-100 px-4 py-2 rounded-lg w-full"
            >
              {t('guestForm.addAccompanying')}
            </button>
          </div>
          <button
            type="submit"
            disabled={isLoading}
            className={`w-full px-4 py-2 rounded-lg ${
              isLoading ? 'bg-gray-400' : 'bg-royal-gold-600 hover:bg-royal-gold-800'
            } text-castle-cream-100`}
          >
            {isLoading ? t('guestForm.processing') : t('guestForm.submitButton')}
          </button>
        </form>
      </div>

      <Modal isOpen={isModalOpen} onClose={handleCloseModal}>
        <div className="text-center">
          <div className={`mx-auto flex items-center justify-center h-12 w-12 rounded-full mb-4 ${
            isSuccess ? 'bg-green-100' : 'bg-red-100'
          }`}>
            {isSuccess ? (
              <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
          </div>
          <h3 className={`text-lg font-medium mb-2 ${
            isSuccess ? 'text-green-900' : 'text-red-900'
          }`}>
            {isSuccess ? t('guestForm.successTitle') : t('guestForm.errorTitle')}
          </h3>
          {!isSuccess && (
            <p className="text-base text-red-700">
              {message}
            </p>
          )}
          {!isSuccess && (
            <button
              onClick={handleCloseModal}
              className="mt-6 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors duration-200"
            >
              {t('guestForm.closeButton') || 'Cerrar'}
            </button>
          )}
        </div>
      </Modal>
    </section>
  );
};

export default GuestForm;